import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  Star,
  ShieldCheck,
  Clock,
  GraduationCap,
  Compass,
  WhatsappLogo,
  InstagramLogo,
  TiktokLogo,
  Lightning,
} from "@phosphor-icons/react";
import MagneticButton from "@/components/MagneticButton";
import Tilt3D from "@/components/Tilt3D";
import RunwayDivider from "@/components/RunwayDivider";
import PilotCarIcon from "@/components/PilotCarIcon";
import SEOHead from "@/components/SEOHead";

/**
 * LangLanding — single-language landing pages for Turkish (/tr) and English (/en).
 * Mirrors the Dutch homepage structure with translated copy.
 * Designed to be wrapped by SiteLayout from App.js.
 */

const WHATSAPP_NUMBER = "31620933773";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/6ecc2oy7_20251118_232018_0000.jpg";
const HERO_BG =
  "https://customer-assets.emergentagent.com/job_website-rebuild-18/artifacts/v62ig18w_autumn.png";

// ============================================================
// Translations
// ============================================================
const T = {
  tr: {
    code: "tr",
    htmlLang: "tr",
    overline: "Eindhoven & Helmond Sürücü Kursu",
    titleA: "Pilot gibi",
    titleB: "sürün.",
    subtitle:
      "Yapı, sakinlik ve bir tutam lüksle özel direksiyon dersleri. Kendi temponuza göre hazırlanmış; deneyimli eğitmenler eşliğinde kendinden emin bir şekilde yola çıkın.",
    ctaPrimary: "Ücretsiz deneme dersi al",
    ctaSecondary: "Paketleri gör",
    perks: ["Ücretsiz deneme dersi", "1 ücretsiz tekrar sınavı", "Kişisel yaklaşım"],
    flightLog: {
      title: "Uçuş Günlüğü",
      seasonLabel: "Sezon",
      season: "Sonbahar",
      status: "Kalkışa hazır",
    },
    featuresOverline: "Neden AutoPilot?",
    featuresHeading: "Akıllı sür. Güvenli sür.",
    featuresAccent: "Otomatik pilotta sür.",
    features: [
      {
        Icon: GraduationCap,
        title: "Yapılandırılmış dersler",
        text:
          "Her ders bir öncekinin üzerine mantıklı şekilde inşa edilir. Özel manevralardan otoyol sürüşüne kadar, net bir ders planıyla kendinize güveninizi kazanırsınız.",
      },
      {
        Icon: ShieldCheck,
        title: "Deneyimli eğitmenler",
        text:
          "Sakin, açık ve sabırlı. Herkes farklı öğrenir — biz size uyum sağlarız, tersi değil.",
      },
      {
        Icon: Star,
        title: "Eğlenceli kampanyalar & indirimler",
        text:
          "Cazip sezon kampanyaları ve paket indirimleri. Daha fazla ders, daha az maliyet.",
      },
      {
        Icon: Clock,
        title: "Sınav koçluğu",
        text:
          "Standart hazırlık değil. Korku, otoyol veya trafik sezgisi — neyi zor buluyorsanız ona odaklanırız.",
      },
      {
        Icon: Compass,
        title: "Eindhoven & Helmond",
        text:
          "Esnek saatler, yerel uzmanlık. Bölgenin iyi tanıdığımız sokaklarında dersleri haftanıza kolayca yerleştiririz.",
      },
    ],
    pkgOverline: "Paketler",
    pkgHeading: "Economy, Business",
    pkgAccent: "veya First Class?",
    pkgSubtitle:
      "Tüm paketler ücretsiz deneme dersi, sınav ve 1 ücretsiz tekrar sınavı içerir.",
    pkgItems: [
      {
        id: "economy",
        name: "Economy Class",
        price: "€1.800",
        tagline: "Hızlı öğrenenler için",
        lessons: "60 dakikalık 25 ders",
        aura: "#4f8dff",
      },
      {
        id: "business",
        name: "Business Class",
        price: "€2.400",
        tagline: "Konfor ve özgüven",
        lessons: "60 dakikalık 35 ders",
        aura: "#b8bfca",
      },
      {
        id: "first",
        name: "First Class",
        price: "€3.000",
        tagline: "En iyi hazırlık",
        lessons: "60 dakikalık 45 ders",
        aura: "#a78bfa",
        highlight: true,
        badge: "En çok seçilen",
      },
    ],
    pkgFeatures: [
      "Ücretsiz deneme dersi",
      "Sınav dahil",
      "1 ücretsiz tekrar sınavı",
      "Bölgede alma servisi",
    ],
    pkgCta: "WhatsApp ile bilgi al",
    takeoffOverline: "Kalkışa hazır",
    takeoffTitle: "Bir sonraki adıma",
    takeoffAccent: "hazır mısınız?",
    takeoffText:
      "Ücretsiz deneme dersinizi planlayın ve öğrencilerimizin neden gülerek ehliyet aldığını keşfedin. Hiçbir yükümlülük yok, sadece deneyim.",
    contactOverline: "İletişim",
    contactTitle: "Bizimle",
    contactAccent: "konuşun.",
    contactText:
      "Sorularınız mı var? WhatsApp üzerinden yazın — Türkçe yanıt veriyoruz. Veya bizi Instagram/TikTok'tan takip edin.",
    contactWA: "WhatsApp'tan yaz",
  },
  en: {
    code: "en",
    htmlLang: "en",
    overline: "Driving School Eindhoven & Helmond",
    titleA: "Drive like",
    titleB: "a pilot.",
    subtitle:
      "Personal driving lessons with structure, calm, and a touch of luxury. Step confidently onto the road — guided by experienced instructors, tailored to your pace.",
    ctaPrimary: "Book a free trial lesson",
    ctaSecondary: "View packages",
    perks: ["Free trial lesson", "1 free re-exam", "Personal approach"],
    flightLog: {
      title: "Flight Log",
      seasonLabel: "Season",
      season: "Autumn",
      status: "Cleared for takeoff",
    },
    featuresOverline: "Why AutoPilot?",
    featuresHeading: "Drive smart. Drive safe.",
    featuresAccent: "Drive on Autopilot.",
    features: [
      {
        Icon: GraduationCap,
        title: "Structured lessons",
        text:
          "Each lesson builds logically on the last. From special manoeuvres to motorway driving — with a clear lesson plan that grows your confidence.",
      },
      {
        Icon: ShieldCheck,
        title: "Experienced instructors",
        text:
          "Calm, clear, patient. Everyone learns differently — we adapt to you, not the other way around.",
      },
      {
        Icon: Star,
        title: "Fun deals & discounts",
        text:
          "Attractive seasonal promotions and package discounts. More lessons, lower cost.",
      },
      {
        Icon: Clock,
        title: "Exam coaching",
        text:
          "No standard prep. Whether it's exam nerves, motorway driving or traffic insight — we focus exactly on what's hard for you.",
      },
      {
        Icon: Compass,
        title: "Eindhoven & Helmond",
        text:
          "Flexible hours, local expertise. We know the regional roads well and slot lessons easily into your week.",
      },
    ],
    pkgOverline: "Packages",
    pkgHeading: "Economy, Business",
    pkgAccent: "or First Class?",
    pkgSubtitle:
      "All packages include a free trial lesson, the practical exam and 1 free re-exam.",
    pkgItems: [
      {
        id: "economy",
        name: "Economy Class",
        price: "€1.800",
        tagline: "For the fast learner",
        lessons: "25 lessons of 60 minutes",
        aura: "#4f8dff",
      },
      {
        id: "business",
        name: "Business Class",
        price: "€2.400",
        tagline: "Comfort and confidence",
        lessons: "35 lessons of 60 minutes",
        aura: "#b8bfca",
      },
      {
        id: "first",
        name: "First Class",
        price: "€3.000",
        tagline: "Maximum preparation",
        lessons: "45 lessons of 60 minutes",
        aura: "#a78bfa",
        highlight: true,
        badge: "Most chosen",
      },
    ],
    pkgFeatures: [
      "Free trial lesson",
      "Practical exam included",
      "1 free re-exam",
      "Pickup service in the region",
    ],
    pkgCta: "Request via WhatsApp",
    takeoffOverline: "Cleared for takeoff",
    takeoffTitle: "Ready for the",
    takeoffAccent: "next step?",
    takeoffText:
      "Book your free trial lesson and discover why our students earn their licence with a smile. No commitments, just experience.",
    contactOverline: "Contact",
    contactTitle: "Get in",
    contactAccent: "touch.",
    contactText:
      "Questions? Send us a WhatsApp message — we reply in English. Or follow us on Instagram and TikTok.",
    contactWA: "Message on WhatsApp",
  },
};

// ============================================================
// Reusable Hero
// ============================================================
const LangHero = ({ t }) => {
  const waMsg = encodeURIComponent(
    t.code === "tr"
      ? "Merhaba! Ücretsiz deneme dersi için bilgi almak istiyorum."
      : "Hi! I'd like info about booking a free trial lesson."
  );
  return (
    <section
      className="relative min-h-screen flex items-center pt-24 overflow-hidden noise"
      data-testid={`hero-${t.code}`}
    >
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="absolute w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2b]/75 via-[#1a1f2b]/55 to-[#1a1f2b]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1f2b] via-[#1a1f2b]/30 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center w-full py-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="runway-stripes w-24 block" />
            <span className="overline">{t.overline}</span>
          </div>

          <h1
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter font-light leading-[0.95] mb-6"
            data-testid={`hero-title-${t.code}`}
          >
            {t.titleA} <br />
            <span className="font-display-italic text-shimmer text-7xl sm:text-8xl md:text-9xl lg:text-[10rem] leading-[0.9]">
              {t.titleB}
            </span>
          </h1>

          <p className="text-lg md:text-xl text-silver max-w-xl mb-10 leading-relaxed">
            {t.subtitle}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <MagneticButton>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid={`hero-cta-primary-${t.code}`}
              >
                {t.ctaPrimary}{" "}
                <ArrowRight size={18} weight="bold" className="anim-arrow" />
              </a>
            </MagneticButton>
            <MagneticButton>
              <a
                href="#packages"
                className="btn-ghost"
                data-testid={`hero-cta-secondary-${t.code}`}
              >
                {t.ctaSecondary}
              </a>
            </MagneticButton>
          </div>

          <div className="mt-16 flex flex-wrap items-center gap-8 text-sm text-silver">
            {t.perks.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle size={18} weight="duotone" className="text-gold anim-check" />
                {p}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="lg:col-span-5 hidden lg:block"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[rgba(212,175,55,0.25)] to-transparent blur-3xl" />
            <div className="relative card-highlight scan-line p-10 flex flex-col gap-6 overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="overline">{t.flightLog.title}</span>
                <Compass size={22} className="text-gold anim-compass" weight="duotone" />
              </div>
              <img src={LOGO_URL} alt="Logo" className="w-40 h-40 mx-auto rounded-full object-cover" />
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs text-silver">
                  <span className="tracking-wider uppercase">{t.flightLog.seasonLabel}</span>
                  <span className="text-gold text-sm font-semibold">{t.flightLog.season}</span>
                </div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <div className="flex justify-between text-xs text-silver">
                  <span>STATUS</span>
                  <span className="text-gold flex items-center gap-2">
                    <span className="w-2 h-2 bg-gold rounded-full pulse-dot" />
                    {t.flightLog.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="hero-runway" aria-hidden="true" />
    </section>
  );
};

// ============================================================
// Features
// ============================================================
const LangFeatures = ({ t }) => (
  <section className="relative py-24 lg:py-32 px-6 md:px-12" data-testid={`features-${t.code}`}>
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-14 max-w-2xl"
      >
        <div className="overline mb-4">{t.featuresOverline}</div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white">
          {t.featuresHeading}
          <br />
          <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
            {t.featuresAccent}
          </span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {t.features.map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
          >
            <Tilt3D max={5} scale={1.01} className="h-full">
              <div
                className="card p-7 h-full flex flex-col gap-3"
                style={{ transformStyle: "preserve-3d" }}
              >
                <f.Icon size={28} weight="duotone" className="text-gold" />
                <h3 className="text-xl text-white tracking-tight">{f.title}</h3>
                <p className="text-sm text-silver leading-relaxed">{f.text}</p>
              </div>
            </Tilt3D>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// Packages
// ============================================================
const LangPackages = ({ t }) => {
  const buildWA = (pkgName) =>
    encodeURIComponent(
      t.code === "tr"
        ? `Merhaba! ${pkgName} paketi hakkında bilgi almak istiyorum.`
        : `Hi! I'd like info about the ${pkgName} package.`
    );

  return (
    <section
      id="packages"
      className="relative py-24 lg:py-32 px-6 md:px-12 bg-secondary"
      data-testid={`packages-${t.code}`}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14 max-w-2xl"
        >
          <div className="overline mb-4">{t.pkgOverline}</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white">
            {t.pkgHeading}
            <br />
            <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
              {t.pkgAccent}
            </span>
          </h2>
          <p className="text-silver mt-6 leading-relaxed">{t.pkgSubtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {t.pkgItems.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Tilt3D max={6} scale={1.015} className="h-full">
                <div
                  className={`p-8 lg:p-10 flex flex-col h-full pkg-aura ${
                    pkg.highlight ? "pkg-aura-on gold-tracer" : ""
                  }`}
                  style={{ "--aura": pkg.aura, transformStyle: "preserve-3d" }}
                  data-testid={`package-card-${t.code}-${pkg.id}`}
                >
                  {pkg.highlight && pkg.badge && (
                    <div className="overline mb-4 flex items-center gap-2">
                      <Star size={12} weight="fill" className="anim-twinkle" />
                      {pkg.badge}
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-gold anim-salute">
                      <PilotCarIcon size={36} weight={pkg.highlight ? "fill" : "duotone"} />
                    </span>
                    <span className="text-xs text-silver tracking-wider uppercase">{pkg.tagline}</span>
                  </div>

                  <h3 className="text-2xl md:text-3xl text-white tracking-tight mb-2">
                    {pkg.name}
                  </h3>
                  <div className="text-5xl font-extralight text-white mb-1">
                    {pkg.price}
                    <span className="text-base text-silver">,-</span>
                  </div>
                  <div className="text-sm text-silver mb-8">{pkg.lessons}</div>

                  <ul className="space-y-3 mb-10 text-sm">
                    {t.pkgFeatures.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-silver">
                        <CheckCircle
                          size={18}
                          weight="duotone"
                          className="text-gold flex-shrink-0 mt-0.5"
                        />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto">
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${buildWA(pkg.name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full justify-center ${
                        pkg.highlight ? "btn-gold" : "btn-ghost"
                      }`}
                      data-testid={`package-cta-${t.code}-${pkg.id}`}
                    >
                      <WhatsappLogo size={18} weight="bold" /> {t.pkgCta}
                    </a>
                  </div>
                </div>
              </Tilt3D>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// Takeoff CTA + Contact
// ============================================================
const LangTakeoff = ({ t }) => {
  const waMsg = encodeURIComponent(
    t.code === "tr"
      ? "Merhaba! Bir deneme dersi planlamak istiyorum."
      : "Hi! I'd like to schedule a trial lesson."
  );
  return (
    <section className="relative overflow-hidden takeoff-bg" data-testid={`takeoff-${t.code}`}>
      <div className="absolute inset-0 bg-gradient-to-b from-[#10141d] via-transparent to-[#10141d]" />
      <div className="hero-runway" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-32 md:py-44 text-center">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-10 h-px bg-gold/70" />
            <span className="overline">{t.takeoffOverline}</span>
            <span className="w-10 h-px bg-gold/70" />
          </div>

          <h2 className="text-5xl sm:text-6xl md:text-7xl tracking-tighter font-light text-white leading-[1] mb-4">
            {t.takeoffTitle}
          </h2>
          <h2 className="font-display-italic text-6xl sm:text-7xl md:text-8xl text-gold leading-[1] mb-10">
            {t.takeoffAccent}
          </h2>

          <p className="text-silver text-base md:text-lg max-w-2xl mx-auto mb-12 leading-relaxed">
            {t.takeoffText}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid={`takeoff-cta-${t.code}`}
              >
                {t.ctaPrimary}{" "}
                <ArrowRight size={18} weight="bold" className="anim-arrow" />
              </a>
            </MagneticButton>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const LangContact = ({ t }) => {
  const waMsg = encodeURIComponent(
    t.code === "tr" ? "Merhaba! Sorum var." : "Hi! I have a question."
  );
  return (
    <section className="relative py-24 lg:py-32 px-6 md:px-12" data-testid={`contact-${t.code}`}>
      <div className="max-w-4xl mx-auto text-center">
        <div className="overline mb-4">{t.contactOverline}</div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white mb-6">
          {t.contactTitle}{" "}
          <span className="font-display-italic text-gold text-5xl md:text-6xl lg:text-7xl">
            {t.contactAccent}
          </span>
        </h2>
        <p className="text-silver leading-relaxed mb-10 text-lg">{t.contactText}</p>

        <div className="flex flex-wrap items-center justify-center gap-4">
          <MagneticButton>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
              data-testid={`contact-wa-${t.code}`}
            >
              <WhatsappLogo size={18} weight="bold" /> {t.contactWA}
            </a>
          </MagneticButton>
        </div>

        <div className="mt-12 flex items-center justify-center gap-6 text-silver">
          <a
            href="https://instagram.com/rijschoolautopilot"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gold transition-colors"
            aria-label="Instagram"
          >
            <InstagramLogo size={24} className="anim-social" />
          </a>
          <a
            href="https://tiktok.com/@autopilot-sd"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gold transition-colors"
            aria-label="TikTok"
          >
            <TiktokLogo size={24} className="anim-social" />
          </a>
        </div>

        {/* Back to Dutch */}
        <div className="mt-16 pt-8 border-t border-white/10 text-sm text-silver/70 flex items-center justify-center gap-3">
          <span>{t.code === "tr" ? "Hollandaca site" : "Dutch site"}</span>
          <Link to="/" className="lang-pill" data-testid={`back-to-nl-${t.code}`}>
            NL
          </Link>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// Spoed callout — small strip linking to the localized crash course
// ============================================================
const SpoedStripe = ({ t }) => {
  const copy = {
    tr: {
      overline: "Hızlandırılmış kurs",
      title: "Ehliyetinizi acil mi lazım?",
      accent: "Hızlandırılmış paketinizi hesaplayın",
      sub: "— çubukları kaydırın ve anında tahmini fiyatı görün.",
      cta: "Hesaplayıcıya git",
    },
    en: {
      overline: "Crash course",
      title: "Need your licence fast?",
      accent: "Build your crash package",
      sub: "— drag the sliders for an instant estimate.",
      cta: "Open the calculator",
    },
  }[t.code];
  return (
    <section className="relative px-6 md:px-12 py-6" data-testid={`spoed-strip-${t.code}`}>
      <div className="max-w-7xl mx-auto">
        <Link
          to={`/${t.code}/spoed`}
          className="group relative block overflow-hidden gold-tracer card-highlight scan-line px-6 sm:px-10 py-6 sm:py-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8"
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div
              className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 grid place-items-center rounded-full"
              style={{
                background: "rgba(224,192,104,0.12)",
                border: "1px solid rgba(224,192,104,0.5)",
                boxShadow: "0 0 22px rgba(224,192,104,0.18)",
              }}
            >
              <Lightning size={20} weight="fill" className="text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-1">{copy.overline}</div>
              <div className="text-white text-lg sm:text-xl tracking-tight leading-snug">
                {copy.title}{" "}
                <span className="font-display-italic text-gold text-xl sm:text-2xl">
                  {copy.accent}
                </span>{" "}
                <span className="text-silver text-sm sm:text-base block sm:inline mt-1 sm:mt-0">
                  {copy.sub}
                </span>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <span className="btn-ghost group-hover:bg-[rgba(224,192,104,0.12)] pointer-events-none">
              {copy.cta}
              <ArrowRight size={16} weight="bold" className="anim-arrow" />
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
};

// ============================================================
// Page exports
// ============================================================
const SITE_URL = "https://www.rijschool-autopilot.nl";

const landingAlternates = [
  { hreflang: "nl", href: `${SITE_URL}/` },
  { hreflang: "tr", href: `${SITE_URL}/tr` },
  { hreflang: "en", href: `${SITE_URL}/en` },
  { hreflang: "x-default", href: `${SITE_URL}/` },
];

export const TurkishLanding = () => {
  return (
    <>
      <SEOHead
        title="Türkçe direksiyon dersi Eindhoven & Helmond | Rijschool AutoPilot"
        description="Eindhoven ve Helmond'da Türkçe konuşan sürücü kursu. Ücretsiz deneme dersi, deneyimli eğitmenler, hızlandırılmış paketler. Drive like a pilot!"
        keywords="türkçe rijschool eindhoven, türkçe direksiyon dersi helmond, türkçe ehliyet kursu, türkçe konuşan eğitmen, türkçe sürücü kursu, autopilot rijschool"
        lang="tr"
        canonical={`${SITE_URL}/tr`}
        ogLocale="tr_TR"
        alternates={landingAlternates}
      />
      <LangHero t={T.tr} />
      <RunwayDivider />
      <LangFeatures t={T.tr} />
      <SpoedStripe t={T.tr} />
      <RunwayDivider />
      <LangPackages t={T.tr} />
      <LangTakeoff t={T.tr} />
      <LangContact t={T.tr} />
    </>
  );
};

export const EnglishLanding = () => {
  return (
    <>
      <SEOHead
        title="English-speaking driving school Eindhoven & Helmond | Rijschool AutoPilot"
        description="English-speaking driving lessons in Eindhoven and Helmond. Free trial lesson, experienced instructors, fast-track crash courses. Drive like a pilot!"
        keywords="english driving school eindhoven, english driving lessons helmond, expat driving school, english speaking instructor netherlands, fast track driving licence, autopilot driving school"
        lang="en"
        canonical={`${SITE_URL}/en`}
        ogLocale="en_US"
        alternates={landingAlternates}
      />
      <LangHero t={T.en} />
      <RunwayDivider />
      <LangFeatures t={T.en} />
      <SpoedStripe t={T.en} />
      <RunwayDivider />
      <LangPackages t={T.en} />
      <LangTakeoff t={T.en} />
      <LangContact t={T.en} />
    </>
  );
};
